import {
    createContext,
    MutableRefObject,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { TransactionsTableMeta } from "@hooks/view/useViewTableMeta";
import { PaginatedTransactionsDataContext } from "@components/transactions/PaginatedTransactionsTable.context";

type OpenCellAction = TransactionsTableMeta["openCellAction"];

interface AccrualReviewTaskFocusControls {
    onFocusedTransactionChange: (transactionId: number | null) => void;
    openCellActionRef: MutableRefObject<OpenCellAction>;
}

const AccrualReviewTaskFocusControlsContext =
    createContext<AccrualReviewTaskFocusControls | null>(null);
const AccrualReviewTaskFocusedIdContext = createContext<number | null>(null);

/**
 * Holds the table's focused row for the accrual task bottom bar. The controls
 * stay referentially stable so the table never re-renders on focus changes;
 * only readers of the focused id do.
 */
export const AccrualReviewTaskFocusProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const [focusedTransactionId, setFocusedTransactionId] = useState<
        number | null
    >(null);
    const openCellActionRef = useRef<OpenCellAction>(undefined);
    const controls = useMemo(
        () => ({
            onFocusedTransactionChange: setFocusedTransactionId,
            openCellActionRef,
        }),
        [],
    );

    return (
        <AccrualReviewTaskFocusControlsContext.Provider value={controls}>
            <AccrualReviewTaskFocusedIdContext.Provider
                value={focusedTransactionId}
            >
                {children}
            </AccrualReviewTaskFocusedIdContext.Provider>
        </AccrualReviewTaskFocusControlsContext.Provider>
    );
};

/** Stable props the task body passes to `TransactionsView`. */
export const useAccrualReviewTaskFocusControls = () => {
    const controls = useContext(AccrualReviewTaskFocusControlsContext);
    if (!controls) {
        throw new Error(
            "AccrualReviewTaskFocusProvider is missing above this component",
        );
    }
    return controls;
};

/**
 * The focused row, or null when nothing is focused or the row left the
 * current page, plus a way to open one of its cell actions.
 */
export const useAccrualReviewTaskFocus = () => {
    const { openCellActionRef } = useAccrualReviewTaskFocusControls();
    const focusedTransactionId = useContext(AccrualReviewTaskFocusedIdContext);
    const { transactions } = useContext(PaginatedTransactionsDataContext);

    const focusedTransaction: TransactionListItem | null = useMemo(
        () =>
            transactions?.find(
                (transaction) => transaction.id === focusedTransactionId,
            ) ?? null,
        [transactions, focusedTransactionId],
    );

    const openFocusedCellAction = useCallback(
        (columnId: string) => {
            if (!focusedTransaction) {
                return;
            }
            openCellActionRef.current?.({
                columnId,
                rowId: focusedTransaction.id,
            });
        },
        [focusedTransaction, openCellActionRef],
    );

    return { focusedTransaction, openFocusedCellAction };
};
